import { useTranslations } from "next-intl";
import CheckIcon from "../_icons/checkIcon";
import DashInCircleIcon from "../_icons/DashInCircleIcon";
import ErrorCircleIcon from "../_icons/ErrorCircleIcon";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { ReorderUnavailableItem } from "@/utils/reorderValidation";
import ReorderWarningIcon from "../_icons/ReorderWarningIcon";
import CloseIcon from "../_icons/closeIcon";
import { isEmpty } from "lodash";
import { useSearchParams } from "next/navigation";

interface BannerProps {
  count: number;
  message?: string;
}

interface UnavailableBannerProps extends BannerProps {
  needsReAdd?: ReorderUnavailableItem[];
  hardUnavailable?: ReorderUnavailableItem[];
  dayPartName?: string;
  onDismiss?: () => void;
}

export const SuccessBanner = ({ count, message }: BannerProps) => {
  const t = useTranslations("SuccessBanner");
  return (
    <div className="rounded-xl mx-4 bg-[#F2FCEE] border-[#276A16] border-[1px] p-4 flex items-center mt-6">
      <CheckIcon />
      <span
        className="ml-4"
        data-testid={applyDataTestId(
          pom.drawer.itemsAvailableAndAddedBannerText
        )}
      >
        {message || t("itemsAddedToCart", { count })}
      </span>
    </div>
  );
};

export const UnavailableBanner = ({
  count,
  message,
  needsReAdd,
  hardUnavailable,
  dayPartName,
  onDismiss,
}: UnavailableBannerProps) => {
  const t = useTranslations("UnavailableBanner");
  const searchParams = useSearchParams();
  const menuName = searchParams?.get("menu");
  const queryParams = new URLSearchParams();
  if (menuName) queryParams.set("menu", menuName || "");
  const paramsString = queryParams.toString();
  const isReorderScenario =
    dayPartName !== undefined ||
    needsReAdd !== undefined ||
    hardUnavailable !== undefined;

  let bannerIcon;
  if (isReorderScenario && count > 0) {
    bannerIcon = <ReorderWarningIcon />;
  } else if (count > 0) {
    bannerIcon = <DashInCircleIcon />;
  } else {
    bannerIcon = <ErrorCircleIcon />;
  }

  let countHeader: string;
  if (count > 0) {
    countHeader = t("reorderSomeAdded", { count });
  } else if (isEmpty(needsReAdd) && dayPartName) {
    countHeader = t("reorderDayPartTitle");
  } else {
    countHeader = t("reorderNoneAdded");
  }

  const fallbackContent =
    message ||
    (count > 0 ? t("itemsUnavailable", { count }) : t("allItemsUnavailable"));

  return (
    <div
      className={`rounded-xl mx-4 ${
        count > 0
          ? "bg-[#FFF6CC] border-[#FFD100]"
          : "bg-[#FFEDED] border-[#E70000]"
      } border-[1px] p-4 flex items-start`}
    >
      <span className="shrink-0">{bannerIcon}</span>
      <div
        className="ml-4 flex-1"
        data-testid={applyDataTestId(pom.drawer.unavailableItemsBannerText)}
      >
        {isReorderScenario ? (
          <>
            <div className="flex items-start justify-between gap-2">
              <p className="font-bold">{countHeader}</p>
              {onDismiss && (
                <button
                  onClick={onDismiss}
                  className="shrink-0 -mt-0.5"
                  aria-label={t("dismissBanner")}
                >
                  <CloseIcon />
                </button>
              )}
            </div>
            {needsReAdd && needsReAdd.length > 0 && (
              <p className="mt-1 pr-8">
                {t("reorderNeedsReAddHeader")}{" "}
                {needsReAdd.map((item, i) => (
                  <span key={item.id}>
                    <a
                      href={`${item.url}${
                        paramsString ? `?${queryParams.toString()}` : ""
                      }`}
                      className="text-[#2A71B1] underline font-medium"
                      data-testid={applyDataTestId(
                        pom.drawer.reorderNeedsReAddItem
                      )}
                    >
                      {item.name}
                    </a>
                    {i < needsReAdd.length - 1 ? ", " : ""}
                  </span>
                ))}
              </p>
            )}
            {hardUnavailable && hardUnavailable.length > 0 && (
              <p
                className={
                  needsReAdd && needsReAdd.length > 0
                    ? "mt-4 pr-8"
                    : "mt-1 pr-8"
                }
              >
                {(() => {
                  if (needsReAdd?.length === 0 && count === 0) {
                    return t("reorderAllHardUnavailableHeader");
                  }
                  return t("reorderHardUnavailableHeader");
                })()}{" "}
                {hardUnavailable.map((item, i) => (
                  <span
                    key={item.id}
                    className="font-bold"
                    data-testid={applyDataTestId(
                      pom.drawer.reorderHardUnavailableItem
                    )}
                  >
                    {item.name}
                    {i < hardUnavailable.length - 1 ? ", " : ""}
                  </span>
                ))}
              </p>
            )}
          </>
        ) : (
          fallbackContent
        )}
      </div>
    </div>
  );
};
